import { useCallback, useMemo } from 'react';
import { useInterpret, useSelector } from '@xstate/react';

import { createScanToPayMachine } from './scan-to-pay.machine';
import type {
  ScanToPayMachineContext,
  ScanToPayMachineServices,
  SetDefaultPaymentMethodParams,
} from './scan-to-pay.machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to provide an interface to communicate with the Scan to Pay state machine.
 */
export const useScanToPayMachine = (params: UseScanToPayMachineParams) => {
  const {
    fetchData,
    fetchIdentifyToken,
    fetchCredits,
    fetchPaymentToken,
    setDefaultPaymentMethod,
  } = params;

  // ─── State Machine ───────────────────────────────────────────────────

  // eslint-disable-next-line functional/prefer-tacit
  const scanToPayMachine = useMemo(() => createScanToPayMachine(), []);

  // ─── Machine Service ─────────────────────────────────────────────────

  const service = useInterpret(scanToPayMachine, {
    services: {
      fetchData,
      fetchIdentifyToken,
      fetchPaymentToken,
      fetchCredits,
      setDefaultPaymentMethod,
    },
  });

  // ─── Context ─────────────────────────────────────────────────────────

  const context = useSelector(service, (currentState) => currentState.context);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isScanToPayFailed = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('failed');
  });

  const isFetchingData = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return (
      matches('fetching') ||
      matches('ready.paymentToken.fetching') ||
      matches('ready.identifyToken.fetching')
    );
  });

  const isAddingPaymentMethod = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.addingPaymentMethod');
  });

  const isSelectingPaymentMethod = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.paymentSelection');
  });

  const isSettingDefaultPaymentMethod = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.settingDefaultPaymentMethod');
  });

  const isShowingLoyaltyInfo = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.loyaltyInfo');
  });

  const isShowingFaq = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.faq');
  });

  // ─── Actions ─────────────────────────────────────────────────────────

  const start = useCallback(() => {
    service.send({ type: 'START' });
  }, [service]);

  const cancelRequest = useCallback(() => {
    service.send({ type: 'CANCEL_REQUEST' });
  }, [service]);

  const requestAddPayment = useCallback(() => {
    service.send({ type: 'REQUEST_ADD_PAYMENT' });
  }, [service]);

  const requestPaymentSelection = useCallback(() => {
    service.send({ type: 'REQUEST_PAYMENT_SELECTION' });
  }, [service]);

  const switchPaymentMethod = useCallback(
    (paymentMethodId: string) => {
      service.send({
        type: 'SET_DEFAULT_PAYMENT_METHOD',
        params: { paymentMethodId },
      });
    },
    [service],
  );

  const switchUseCredit = useCallback(() => {
    service.send({ type: 'SWITCH_USE_CREDIT' });
  }, [service]);

  const requestLoyaltyInfo = useCallback(() => {
    service.send({ type: 'REQUEST_LOYALTY_INFO' });
  }, [service]);

  const refetchPaymentToken = useCallback(() => {
    service.send({ type: 'REFETCH_PAYMENT_TOKEN' });
  }, [service]);

  const refetchIdentifyToken = useCallback(() => {
    service.send({ type: 'REFETCH_IDENTIFY_TOKEN' });
  }, [service]);

  const refetchCredits = useCallback(() => {
    service.send({ type: 'REFETCH_CREDITS' });
  }, [service]);

  const requestFaq = useCallback(() => {
    service.send({ type: 'REQUEST_FAQ' });
  }, [service]);

  // ─── Response ────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      ...context,
      isScanToPayFailed,
      isFetchingData,
      isAddingPaymentMethod,
      isSelectingPaymentMethod,
      isSettingDefaultPaymentMethod,
      isShowingLoyaltyInfo,
      isShowingFaq,
      start,
      cancelRequest,
      requestAddPayment,
      requestPaymentSelection,
      switchPaymentMethod,
      switchUseCredit,
      requestLoyaltyInfo,
      refetchPaymentToken,
      refetchIdentifyToken,
      refetchCredits,
      requestFaq,
    }),
    [
      context,
      isScanToPayFailed,
      isFetchingData,
      isAddingPaymentMethod,
      isSelectingPaymentMethod,
      isSettingDefaultPaymentMethod,
      isShowingLoyaltyInfo,
      isShowingFaq,
      start,
      cancelRequest,
      requestAddPayment,
      requestPaymentSelection,
      switchPaymentMethod,
      switchUseCredit,
      requestLoyaltyInfo,
      refetchPaymentToken,
      refetchIdentifyToken,
      refetchCredits,
      requestFaq,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScanToPayMachineParams = {
  fetchData: () => Promise<ScanToPayMachineServices['fetchData']['data']>;
  fetchIdentifyToken: () => Promise<
    ScanToPayMachineServices['fetchIdentifyToken']['data']
  >;
  fetchPaymentToken: (
    context: ScanToPayMachineContext,
  ) => Promise<ScanToPayMachineServices['fetchPaymentToken']['data']>;
  fetchCredits: () => Promise<{ availableCredit: number }>;
  setDefaultPaymentMethod: (
    context: ScanToPayMachineContext,
    params: SetDefaultPaymentMethodParams,
  ) => Promise<ScanToPayMachineServices['setDefaultPaymentMethod']['data']>;
};
